import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile, updateUserProfile } from '../services/authService';
import { getUserSubscriptions, cancelSubscription, reactivateSubscription, getPurchaseHistory } from '../services/subscriptionService';
import './Profile.css';

function Profile() {
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);
  const [reactivateModalOpen, setReactivateModalOpen] = useState(false);
  const [subscriptionToReactivate, setSubscriptionToReactivate] = useState(null);
  
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: ''
  });
  
  const { currentUser, updateVerificationStatus } = useAuth();

  // Fetch profile and subscription data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch user profile
        const profile = await getUserProfile();
        setProfileData({
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          email: profile.email || currentUser.email || '',
          phone: profile.phone || '',
          company: profile.company || '',
          jobTitle: profile.job_title || ''
        });
        
        // Fetch user subscriptions
        try {
          const subscriptionsData = await getUserSubscriptions();
          setSubscriptions(subscriptionsData);
        } catch (subError) {
          console.error('Error fetching subscriptions:', subError);
          setSubscriptionError('Failed to load subscription information');
        }

        // Fetch purchase history
        try {
          const purchaseHistoryData = await getPurchaseHistory();
          setPurchaseHistory(purchaseHistoryData);
        } catch (purchaseError) {
          console.error('Error fetching purchase history:', purchaseError);
        }
        
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfileError('Failed to load profile information');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditToggle = () => {
    setEditMode(!editMode);
    
    // If canceling edit, reset form data to original values
    if (editMode) {
      getUserProfile().then(profile => {
        setProfileData({
          firstName: profile.first_name || '',
          lastName: profile.last_name || '',
          email: profile.email || currentUser.email || '',
          phone: profile.phone || '',
          company: profile.company || '',
          jobTitle: profile.job_title || ''
        });
      }).catch(err => {
        console.error('Error resetting profile data:', err);
      });
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setProfileError(null);
      
      const updateData = {
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        phone: profileData.phone,
        company: profileData.company,
        job_title: profileData.jobTitle
      };
      
      await updateUserProfile(updateData);
      setEditMode(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      setProfileError(error.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const openCancelModal = (subscription) => {
    setSubscriptionToCancel(subscription);
    setCancelModalOpen(true);
  };

  const closeCancelModal = () => {
    setCancelModalOpen(false);
    setSubscriptionToCancel(null);
    setCancelReason('');
  };

  const openReactivateModal = (subscription) => {
    setSubscriptionToReactivate(subscription);
    setReactivateModalOpen(true);
  };

  const closeReactivateModal = () => {
    setReactivateModalOpen(false);
    setSubscriptionToReactivate(null);
  };

  const handleCancelSubscription = async () => {
    if (!subscriptionToCancel) return;
    
    try {
      setLoading(true);
      
      await cancelSubscription(subscriptionToCancel.id, cancelReason);
      
      // Refresh subscriptions list
      const updatedSubscriptions = await getUserSubscriptions();
      setSubscriptions(updatedSubscriptions);
      
      closeCancelModal();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setSubscriptionError(error.detail || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!subscriptionToReactivate) return;
    
    try {
      setLoading(true);
      
      await reactivateSubscription(subscriptionToReactivate.id);
      
      // Refresh subscriptions list
      const updatedSubscriptions = await getUserSubscriptions();
      setSubscriptions(updatedSubscriptions);
      
      closeReactivateModal();
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      setSubscriptionError(error.detail || 'Failed to reactivate subscription');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A';
    
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Check if subscription is in trial status and if billing will start immediately upon reactivation
  const willBeChargedImmediately = (subscription) => {
    // If it's not in trial status or trial has already ended
    if (subscription.status !== 'trialing' || 
       (subscription.trial_end_date && new Date(subscription.trial_end_date) < new Date())) {
      return true;
    }
    return false;
  };

  if (loading) {
    return <div className="loading-container">Loading profile information...</div>;
  }

  return (
    <div className="profile-page">
      <div className="container">
        <h1>My Profile</h1>
        
        {profileError && <div className="alert alert-danger">{profileError}</div>}
        {subscriptionError && <div className="alert alert-danger">{subscriptionError}</div>}
        
        <div className="profile-content">
          <div className="profile-section">
            <div className="section-header">
              <h2>Personal Information</h2>
              <button 
                onClick={handleEditToggle} 
                className="btn btn-sm btn-outline"
              >
                {editMode ? 'Cancel' : 'Edit'}
              </button>
            </div>
            
            {editMode ? (
              <form className="profile-form" onSubmit={handleProfileSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name</label>
                    <input
                      id="firstName"
                      name="firstName"
                      value={profileData.firstName}
                      onChange={handleInputChange}
                      placeholder="Enter your first name"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name</label>
                    <input
                      id="lastName"
                      name="lastName"
                      value={profileData.lastName}
                      onChange={handleInputChange}
                      placeholder="Enter your last name"
                      required
                    />
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={profileData.email}
                    disabled
                    className="disabled-input"
                  />
                  <small className="form-text">
                    Email cannot be changed. Contact support for assistance.
                  </small>
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    id="phone"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                  />
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="company">Company</label>
                    <input
                      id="company"
                      name="company"
                      value={profileData.company}
                      onChange={handleInputChange}
                      placeholder="Enter your company"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="jobTitle">Job Title</label>
                    <input
                      id="jobTitle"
                      name="jobTitle"
                      value={profileData.jobTitle}
                      onChange={handleInputChange}
                      placeholder="Enter your job title"
                    />
                  </div>
                </div>
                
                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="profile-info">
                <div className="info-row">
                  <div className="info-group">
                    <span className="info-label">Name</span>
                    <span className="info-value">
                      {profileData.firstName} {profileData.lastName}
                    </span>
                  </div>
                  
                  <div className="info-group">
                    <span className="info-label">Email</span>
                    <span className="info-value">
                      {profileData.email}
                      {currentUser && currentUser.isVerified && 
                        <span className="verified-badge">Verified</span>
                      }
                    </span>
                  </div>
                </div>
                
                <div className="info-row">
                  <div className="info-group">
                    <span className="info-label">Phone</span>
                    <span className="info-value">
                      {profileData.phone || 'Not provided'}
                    </span>
                  </div>
                  
                  <div className="info-group">
                    <span className="info-label">Company</span>
                    <span className="info-value">
                      {profileData.company || 'Not provided'}
                    </span>
                  </div>
                </div>
                
                <div className="info-row">
                  <div className="info-group">
                    <span className="info-label">Job Title</span>
                    <span className="info-value">
                      {profileData.jobTitle || 'Not provided'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <div className="profile-section">
            <div className="section-header">
              <h2 id="subscriptions">My Subscriptions</h2>
            </div>
            
            {subscriptions.length === 0 ? (
              <div className="no-subscriptions">
                <p>You don't have any active subscriptions.</p>
                <Link to="/books" className="btn btn-primary">
                  Browse Packages
                </Link>
              </div>
            ) : (
              <div className="subscriptions-list">
                {subscriptions.map((subscription) => (
                  <div key={subscription.id} className="subscription-card">
                    <div className="subscription-header">
                      <h3>{subscription.package ? subscription.package.name : subscription.package_name}</h3>
                      <div className="status-container">
                        {subscription.status === 'trialing' && subscription.trial_end_date && new Date(subscription.trial_end_date) > new Date() && (
                          <span className="trial-badge">
                            TRIAL • Ends {formatDate(subscription.trial_end_date)}
                          </span>
                        )}
                        <span className={`status-badge status-${subscription.cancel_at_period_end ? 'canceling' : subscription.status.toLowerCase()}`}>
                          {subscription.cancel_at_period_end ? 'CANCELING' : subscription.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    
                    {subscription.status === 'trialing' && subscription.trial_end_date && new Date(subscription.trial_end_date) > new Date() && (
                      <div className="trial-info-box">
                        <div className="trial-info-icon">ⓘ</div>
                        <div className="trial-info-text">
                          <p><strong>You're in a trial period.</strong> Your subscription will automatically convert to a paid plan on <strong>{formatDate(subscription.trial_end_date)}</strong>.</p>
                          <p>You will not be charged until your trial ends.</p>
                        </div>
                      </div>
                    )}
                    
                    <div className="subscription-details">
                      <div className="detail-row">
                        <span className="detail-label">Subscription ID:</span>
                        <span className="detail-value">{subscription.id}</span>
                      </div>
                      
                      <div className="detail-row">
                        <span className="detail-label">Price:</span>
                        <span className="detail-value">{formatCurrency(subscription.package ? subscription.package.price : subscription.price)}</span>
                      </div>
                      
                      <div className="detail-row">
                        <span className="detail-label">Billing Period:</span>
                        <span className="detail-value">{subscription.package ? subscription.package.billing_cycle : 'Monthly'}</span>
                      </div>
                      
                      <div className="detail-row">
                        <span className="detail-label">Start Date:</span>
                        <span className="detail-value">{formatDate(subscription.start_date)}</span>
                      </div>
                      
                      {subscription.trial_end_date && (
                        <div className="detail-row">
                          <span className="detail-label">Trial Ends:</span>
                          <span className="detail-value">
                            {formatDate(subscription.trial_end_date)}
                            {new Date(subscription.trial_end_date) > new Date() && (
                              <span className="trial-active-tag"> (Active)</span>
                            )}
                          </span>
                        </div>
                      )}
                      
                      <div className="detail-row">
                        <span className="detail-label">Next Billing:</span>
                        <span className="detail-value">
                          {subscription.cancel_at_period_end 
                            ? `Cancels on ${formatDate(subscription.current_period_end)}` 
                            : formatDate(subscription.current_period_end || subscription.renewal_date)}
                        </span>
                      </div>
                      
                      <div className="detail-row">
                        <span className="detail-label">Payment Method:</span>
                        <span className="detail-value">
                          {subscription.payment_method?.details 
                            ? `${subscription.payment_method.details.brand?.charAt(0).toUpperCase() + subscription.payment_method.details.brand?.slice(1) || 'Card'} ending in ${subscription.payment_method.details.last4}` 
                            : 'Not available'}
                        </span>
                      </div>
                    </div>
                    
                    <div className="subscription-actions">
                      <Link 
                        to={`/subscription/${subscription.id}/invoices`} 
                        className="btn btn-sm btn-outline"
                      >
                        View Invoices
                      </Link>
                      
                      <Link 
                        to={`/subscription/${subscription.id}/payment`} 
                        className="btn btn-sm btn-outline"
                      >
                        Update Payment
                      </Link>
                      
                      {!subscription.cancel_at_period_end && (
                        <button 
                          onClick={() => openCancelModal(subscription)} 
                          className="btn btn-sm btn-danger"
                        >
                          Cancel Subscription
                        </button>
                      )}
                      
                      {subscription.cancel_at_period_end && (
                        <button 
                          onClick={() => openReactivateModal(subscription)}
                          className="btn btn-sm btn-primary"
                        >
                          Re-Activate Subscription
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="profile-section">
            <div className="section-header">
              <h2>Purchase History</h2>
            </div>
            
            {purchaseHistory.length === 0 ? (
              <div className="no-purchases">
                <p>You haven't made any purchases yet.</p>
                <Link to="/company" className="btn btn-primary">
                  Browse Services
                </Link>
              </div>
            ) : (
              <div className="purchase-history-list">
                {purchaseHistory.map((purchase) => (
                  <div key={purchase.id} className="purchase-card">
                    <div className="purchase-header">
                      <h3>
                        {purchase.package ? purchase.package.name : 
                         purchase.form ? purchase.form.form_name : 
                         'Unknown Purchase'}
                      </h3>
                      <div className="status-container">
                        <span className={`status-badge status-${purchase.status.toLowerCase()}`}>
                          {purchase.status.toUpperCase()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="purchase-details">
                      <div className="detail-row">
                        <span className="detail-label">Purchase ID:</span>
                        <span className="detail-value">{purchase.id}</span>
                      </div>
                      
                      <div className="detail-row">
                        <span className="detail-label">Type:</span>
                        <span className="detail-value">
                          {purchase.package ? 'Package Subscription' : 
                           purchase.form ? 'Form Filing' : 
                           'Unknown Type'}
                        </span>
                      </div>
                      
                      <div className="detail-row">
                        <span className="detail-label">Amount:</span>
                        <span className="detail-value">{formatCurrency(purchase.amount)}</span>
                      </div>
                      
                      <div className="detail-row">
                        <span className="detail-label">Date:</span>
                        <span className="detail-value">{formatDate(purchase.created_at)}</span>
                      </div>
                      
                      {purchase.billing_period_start && purchase.billing_period_end && (
                        <>
                          <div className="detail-row">
                            <span className="detail-label">Billing Period:</span>
                            <span className="detail-value">
                              {formatDate(purchase.billing_period_start)} - {formatDate(purchase.billing_period_end)}
                            </span>
                          </div>
                        </>
                      )}
                      
                      {purchase.payment_method && (
                        <div className="detail-row">
                          <span className="detail-label">Payment Method:</span>
                          <span className="detail-value">
                            {purchase.payment_method.details 
                              ? `${purchase.payment_method.details.brand?.charAt(0).toUpperCase() + purchase.payment_method.details.brand?.slice(1) || 'Card'} ending in ${purchase.payment_method.details.last4}` 
                              : 'Not available'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Cancel Subscription Modal */}
      {cancelModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Cancel Subscription</h3>
              <button onClick={closeCancelModal} className="close-button">&times;</button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to cancel your subscription to {msubscriptionToCancel?.package_name}?</p>
              
              <div className="form-group">
                <label htmlFor="cancelReason">Reason for cancellation (optional)</label>
                <textarea
                  id="cancelReason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Please tell us why you're canceling"
                  rows={4}
                />
              </div>
              
              {subscriptionToCancel?.trial_end_date && new Date(subscriptionToCancel.trial_end_date) > new Date() && (
                <div className="alert alert-info">
                  You are still in your trial period. You won't be charged if you cancel now.
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button onClick={closeCancelModal} className="btn btn-outline">
                Back
              </button>
              <button onClick={handleCancelSubscription} className="btn btn-danger">
                Confirm Cancellation
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Subscription Modal */}
      {reactivateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Re-Activate Subscription</h3>
              <button onClick={closeReactivateModal} className="close-button">&times;</button>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to re-activate your subscription to {subscriptionToReactivate?.package_name}?</p>
              
              {willBeChargedImmediately(subscriptionToReactivate) ? (
                <div className="alert alert-warning">
                  Your credit card will be charged immediately and your subscription will be active right away.
                </div>
              ) : (
                <div className="alert alert-info">
                  Your subscription will continue with your current trial period. Your credit card will not be charged until {formatDate(subscriptionToReactivate?.trial_end_date)}.
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button onClick={closeReactivateModal} className="btn btn-outline">
                Back
              </button>
              <button onClick={handleReactivateSubscription} className="btn btn-primary">
                Confirm Re-Activation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Profile; 